// dictionaryApi.js
import { callAi } from "./geminiClient";

/** 증상 그룹(질문 6개) — 한국어 전용 */
export const DICTIONARY_GROUPS = () => [
  { key: "sns",   title: "교감신경 항진", items: ["심장두근거림","손떨림","열감","땀과다","얼굴붉어짐","두통","불면(입면장애/수면중각성)","오한/손발찬느낌","카페인/스트레스 민감도 증가"] },
  { key: "hydr",  title: "수분 부족",     items: ["목마름/입건조","피부건조/가려움","심한구취/백태","잦은소변(빈뇨)"] },
  { key: "gi",    title: "소화기계 적응", items: ["속쓰림/메스꺼움","소화불량/더부룩함","가스참/복부팽만","변비","설사"] },
  { key: "hypo",  title: "저혈당 증상",   items: ["어지러움/기립성저혈압","피로감/무기력","우울감/기분기복"] },
  { key: "elect", title: "전해질 불균형", items: ["손발저림","눈밑떨림","근육경련(쥐남)"] },
  { key: "etc",   title: "기타",         items: ["목에이물감(캡슐걸린느낌)"] },
];

/** 버전 레이블  — 한국어 */
export const VERSION_META = () => [
  { value: "1", label: "버전1 (기본)" },
  { value: "2", label: "버전2 (상세)" },   // 기본 선택은 페이지 상태에서 이미 "2"로 세팅됨
  { value: "3", label: "버전3 (전문가)" },
];

/** 증상 그룹(질문 6개) — 중국어 전용 */
export const DICTIONARY_GROUPS_CN = () => [
  {
    key: "sns",
    title: "交感神经亢进",
    items: [
      "心悸",
      "手抖",
      "发热感/潮热",
      "多汗",
      "脸部潮红",
      "头痛",
      "失眠（入睡困难/夜间易醒）",
      "恶寒/手脚发凉",
      "对咖啡因/压力敏感度增加",
    ],
  },
  {
    key: "hydr",
    title: "缺水",
    items: ["口渴/口干", "皮肤干燥/瘙痒", "口臭明显/舌苔发白", "尿频（小便频繁）"],
  },
  {
    key: "gi",
    title: "消化系统适应",
    items: ["烧心/恶心", "消化不良/腹胀", "积气/腹部胀满", "便秘", "腹泻"],
  },
  {
    key: "hypo",
    title: "低血糖症状",
    items: ["头晕/体位性低血压", "疲劳/无力", "抑郁/情绪波动"],
  },
  {
    key: "elect",
    title: "电解质失衡",
    items: ["手脚麻木", "眼皮跳动", "肌肉痉挛（抽筋）"],
  },
  {
    key: "etc",
    title: "其他",
    items: ["咽喉异物感（像胶囊卡住）"],
  },
];

/** 버전 레이블 — 중국어 전용 */
export const VERSION_META_CN = () => [
  { value: "1", label: "版本1（基础）" },
  { value: "2", label: "版本2（详细）" },
  { value: "3", label: "版本3（专家）" },
];

/** 프롬프트(원문 유지: 한국어 기본) */
export function buildDictionaryPrompt(_lang, symptoms, version = "2") {
  const joined = symptoms.join(", ");

  const commonHeader = `You are an expert AI assistant for '${process.env.REACT_APP_CLINIC_NAME}', You are responding to a patient who is on our herbal medicine diet program. The patient is taking our main diet medicine (either '${process.env.REACT_APP_JIBANG_SAYACK}' or '${process.env.REACT_APP_SIKTAM_SAYAK}') which works by activating the sympathetic nervous system. They may also be taking supplementary capsules like '${process.env.REACT_APP_DOKSO_KILL}' for detoxification, '${process.env.REACT_APP_SUMYEON_KILL}' for sleep, and '${process.env.REACT_APP_JIBANG_KILL}' for abdominal fat.

The patient has selected the following symptoms: ${joined}.

Generate an analysis in KOREAN, structured with "🩺 원인 분석" and "💡 맞춤 관리 팁" for each relevant symptom group. The tone must be professional, reassuring, and empathetic. Do NOT include any meal recommendations. The entire response must be in polite, formal Korean. When explaining the management tips, refer to our specific products like '${process.env.REACT_APP_DOKSO_KILL}' or the main '${process.env.REACT_APP_SIKTAM_KILL}' capsule when appropriate (e.g., 'adjusting the '${process.env.REACT_APP_DOKSO_KILL}' dosage' or 'removing one '${process.env.REACT_APP_SIKTAM_KILL}' capsule').`;

  const v1 = `**Version 1 (Basic) Analysis):** ...`;
  const v2 = `**Version 2 (Detailed) Analysis:** ...`;
  const v3 = `**Version 3 (Expert) Analysis:** ...`;

  const detail = version === "1" ? v1 : version === "3" ? v3 : v2;
  return `${commonHeader}\n\n${detail}`;
}

/** 보조 기능: 증상+분석 기반 하루 식단(한국어 기본) */
export function buildMealPlanPrompt(_lang, symptoms, analysis) {
  const joined = symptoms.join(", ");
  return `You are the same AI assistant from before. The patient, who is experiencing symptoms like [${joined}], has just received your initial analysis which is: "${analysis}".

Now, based on both the symptoms and your analysis, the patient wants a practical, one-day sample meal plan (breakfast, lunch, dinner).

This meal plan MUST:
1.  Be in KOREAN.
2.  Align with the '${process.env.REACT_APP_CLINIC_NAME}' diet program principles (generally low-carb, anti-inflammatory, focused on whole foods).
3.  Specifically help alleviate the patient's reported symptoms.
4.  Be simple and easy to prepare.

Start with a brief, encouraging sentence. Then, present the meal plan in a clear, structured format using Markdown with headings for '아침', '점심', '저녁'. For each meal, provide 1-2 simple menu items.`;
}

/** 사전 분석 프롬프트 (ko / zh-CN 분기) */
export function buildDictionaryPromptIntl(lang, symptoms, version = "2") {
  if (lang === "zh-CN") {
    const joined = symptoms.join(", ");
    const commonHeader = `You are an expert AI assistant for '${process.env.REACT_APP_CN_CLINIC_NAME}', You are responding to a patient who is on our herbal medicine diet program. The patient is taking our main diet medicine (either '${process.env.REACT_APP_CN_JIBANG_SAYACK}' or '${process.env.REACT_APP_CN_SIKTAM_SAYAK}') which works by activating the sympathetic nervous system. They may also be taking supplementary capsules like '${process.env.REACT_APP_CN_DOKSO_KILL}' for detoxification, '${process.env.REACT_APP_CN_SUMYEON_KILL}' for sleep, and '${process.env.REACT_APP_CN_JIBANG_KILL}' for abdominal fat.

The patient has selected the following symptoms: ${joined}.

Generate an analysis in Chinese, structured with "🩺 原因分析" and "💡 个性化管理提示" for each relevant symptom group. The tone must be professional, reassuring, and empathetic. Do NOT include any meal recommendations. The entire response must be in polite, formal Chinese. When explaining the management tips, refer to our specific products like '独素杀(${process.env.REACT_APP_CN_DOKSO_KILL})' or the main '食欲杀(${process.env.REACT_APP_CN_SIKTAM_KILL})' capsule when appropriate (e.g., 'adjusting the ${process.env.REACT_APP_CN_DOKSO_KILL} dosage' or 'removing one ${process.env.REACT_APP_CN_SIKTAM_KILL} capsule').`;

    const v1Detail = `**Version 1 (基础) 分析:** 用简单易懂的方式解释原因。例如："你的心跳加快是因为 '${process.env.REACT_APP_CN_JIBANG_SAYACK}' 正在促进代谢，就像运动时一样。" 对于便秘，可以提到 "'${process.env.REACT_APP_CN_DOKSO_KILL}' 可以帮助缓解这一问题。"`;
    const v2Detail = `**Version 2 (详细) 分析:** 更深入地解释生理机制。说明 '${process.env.REACT_APP_CN_JIBANG_SAYACK}' 或 '${process.env.REACT_APP_CN_SIKTAM_SAYAK}' 如何激活交感神经系统，导致儿茶酚胺释放。对消化问题，解释 '${process.env.REACT_APP_CN_DOKSO_KILL}' 的促动力作用。对睡眠问题，提到主要药物可能引起失眠，而 '${process.env.REACT_APP_CN_SUMYEON_KILL}' 可以作为有益的补充。`;
    const v3Detail = `**Version 3 (专家级) 分析:** 提供超详细的大学水平医学解释。
* **SNS 症状:** 说明 '${process.env.REACT_APP_CN_JIBANG_SAYACK}' 和 '${process.env.REACT_APP_CN_SIKTAM_SAYAK}' 中的拟交感化合物如何引起儿茶酚胺释放、β-肾上腺素能受体结合、UCP1 介导的产热以及外周血管收缩。讨论失眠时，说明其对褪黑素的抑制，并提到 '${process.env.REACT_APP_CN_SUMYEON_KILL}' 可通过促进放松和增强睡眠时产热来对抗。
* **GI 症状:** 对便秘/腹泻，详细说明 '${process.env.REACT_APP_CN_DOKSO_KILL}' 作为促动力剂的机制，可根据患者排便情况进行调整。
* **其他机制:** 解释脂解作用、渗透性利尿、电解质消耗和相对低血糖，作为主要药物代谢效应的直接或间接结果。`;
    const detail = version === "1" ? v1Detail : version === "3" ? v3Detail : v2Detail;
    return `${commonHeader}\n\n${detail}`;
  }
  // 한국어 기본
  return buildDictionaryPrompt(lang, symptoms, version);
}

/** 식단 프롬프트 (ko / zh-CN 분기) */
export function buildMealPlanPromptIntl(lang, symptoms, analysis) {
  if (lang === "zh-CN") {
    const joined = symptoms.join(", ");
    return `You are the same AI assistant from before. The patient, who is experiencing symptoms like [${joined}], has just received your initial analysis which is: "${analysis}".

Now, based on both the symptoms and your analysis, the patient wants a practical, one-day sample meal plan (breakfast, lunch, dinner).

This meal plan MUST:
1.  Be in Chinese.
2.  Align with the '${process.env.REACT_APP_CN_CLINIC_NAME}' diet program principles (generally low-carb, anti-inflammatory, focused on whole foods).
3.  Specifically help alleviate the patient's reported symptoms.
4.  Be simple and easy to prepare.

Start with a brief, encouraging sentence. Then, present the meal plan in a clear, structured format using Markdown with headings for '早餐', '午餐', '晚餐'. For each meal, provide 1-2 simple menu items.`;
  }
  // 한국어 기본
  return buildMealPlanPrompt(lang, symptoms, analysis);
}

/** 간단 Markdown → HTML */
export function renderMarkdown(text) {
  const blocks = String(text).trim().split(/\n\s*\n/);
  return blocks
    .map((block) => {
      if (block.startsWith("### ")) return `<h3>${block.substring(4).replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")}</h3>`;
      if (block.startsWith("#### ")) return `<h4>${block.substring(5).replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")}</h4>`;
      if (block.startsWith("* ")) {
        const items = block
          .split("\n")
          .map((line) => `<li>${line.replace(/^\*\s*/, "").replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")}</li>`)
          .join("");
        return `<ul>${items}</ul>`;
      }
      return `<p>${block.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/\n/g, "<br>")}</p>`;
    })
    .join("");
}

/** 메인 사전 분석 호출 */
export const DictionaryAI = {
  async analyze({ symptoms, version }) {
    const lang = (typeof document !== "undefined" && document.documentElement?.lang) || "ko";
    const userStr = (typeof localStorage !== "undefined" && localStorage.getItem("user")) || "";
    const user = userStr ? JSON.parse(userStr) : null;
    if (!user) {
      alert("로그인 후 사용해 주세요");
      if (typeof window !== "undefined") window.location.href = "/login";
      throw new Error("NOT_AUTHENTICATED");
    }

    // ✅ 언어 분기형 프롬프트 사용 (Review API 스타일)
    const prompt = buildDictionaryPromptIntl(lang, symptoms, version);
    const contents = [{ role: "user", parts: [{ text: prompt }] }];

    // 라우팅 키 유지
    const data = await callAi({
      contents,
      api_name: "profiling",
      version: undefined,
      generationConfig: null,
      userId: user.id,
      meta: { source: "dictionary" },
    });

    const answer = data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text ?? "";
    if (!answer) throw new Error("응답 형식이 올바르지 않습니다.");
    return { answer, html: renderMarkdown(answer) };
  },

  async mealPlan({ symptoms, analysis }) {
    const lang = (typeof document !== "undefined" && document.documentElement?.lang) || "ko";
    const userStr = (typeof localStorage !== "undefined" && localStorage.getItem("user")) || "";
    const user = userStr ? JSON.parse(userStr) : null;
    if (!user) {
      alert("로그인 후 사용해 주세요");
      if (typeof window !== "undefined") window.location.href = "/login";
      throw new Error("NOT_AUTHENTICATED");
    }

    // ✅ 언어 분기형 프롬프트 사용 (Review API 스타일)
    const prompt = buildMealPlanPromptIntl(lang, symptoms, analysis);
    const contents = [{ role: "user", parts: [{ text: prompt }] }];

    const data = await callAi({
      contents,
      api_name: "profiling",     // 동일 키 사용
      version: undefined,
      generationConfig: null,
      userId: user.id,
      meta: { source: "dictionary:mealplan" },
    });

    const answer = data?.data?.answer ?? data?.answer ?? data?.data?.text ?? data?.text ?? "";
    if (!answer) throw new Error("응답 형식이 올바르지 않습니다.");
    return { answer, html: renderMarkdown(answer) };
  },
};
